import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { setPrice } from "../../../server/Product";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { GetPromotions, updatePromotion } from "../../../server/PriceList";
import FamilyChangeDialog from "./FamilyChangeDialog";
import "./ComparePricePopUp.scss";
import PricesCompareTable from "./PricesCompareTable";
import Promotion from "./Promotion";
import SupplierPricesSection from "./SupplierPricesSection";
import PopUp from "../../../components/containers/PopUp";
import {
  Pencil,
  Plus,
  Calendar,
  Check,
  History,
  StopCircle,
} from "lucide-react";
import ReactTooltip from "react-tooltip";
import {
  calcProfitMargin,
  effectivePriceFromMargin,
  getCalculatedPromoSalePrice,
  getProfitMarginVatHint,
  isFlatPromoType,
  isSamePriceValue,
  pickDisplayPromotion,
} from "./priceMarginUtils";
import { getFamilyCategory } from "./familyCategoryUtils";
import { applySequentially } from "./useFamilyProductUpdate";
import { getProductHeaderMetaParts } from "../../../utils/formatProductPackSize";
import { formatExpirationDate } from "../../../utils/formatExpirationDate";
import {
  formatDateTimeForJerusalem,
  getPromoMinQuantityNote,
  getPromotionApiErrorMessage,
} from "../sales/promotionUtils";
import { roundMoney } from "../../../utils/formatPrice";

const getJerusalemDateOnly = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

/** End date for ending a promo: the most recent Jerusalem EOD boundary that is strictly in the past. */
const buildEndOfferPayload = (promo) => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const endDay = getJerusalemDateOnly(yesterday);
  const endDate = formatDateTimeForJerusalem(endDay, { endOfDay: true });

  const payload = { endDate, status: "EXPIRED" };
  const startMs = Date.parse(promo?.startDate);
  const endMs = Date.parse(endDate);
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && startMs >= endMs) {
    payload.startDate = formatDateTimeForJerusalem(endDay, { endOfDay: false });
  }
  return payload;
};

const ComparePricePopUp = ({
  product: productProp,
  setComparePricePopUp,
  onPriceSaved,
  onPromotionsChanged,
  isModal = false,
}) => {
  const [sellingPrice, setSellingPrice] = useState("");
  const [savedSellingPrice, setSavedSellingPrice] = useState(null);
  const [profitMargin, setProfitMargin] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [savedSalePrice, setSavedSalePrice] = useState(null);
  const [saleProfitMargin, setSaleProfitMargin] = useState("");
  const [savingSalePrice, setSavingSalePrice] = useState(false);
  const [costPrice, setCostPrice] = useState(null);
  const [showPricePopUp, setShowPricePopUp] = useState(false);
  const [showPromotionPopUp, setShowPromotionPopUp] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [showPromoHistory, setShowPromoHistory] = useState(false);
  const [endingPromoId, setEndingPromoId] = useState(null);

  const stationId = useSelector((state) => state.user.stationId);
  const selectedProduct = useSelector((state) => state.selectedProduct.product);
  const pricing = useSelector((state) => state.selectedProduct.productPricing);
  const categories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );

  const product =
    productProp &&
    productProp.productId != null &&
    String(selectedProduct?.productId) === String(productProp.productId)
      ? selectedProduct
      : productProp || selectedProduct;

  const familyCategory = getFamilyCategory(categories);
  const vatExempt = Boolean(product?.vatExempt);
  const productHeaderMetaParts = getProductHeaderMetaParts(product);
  const currentPromotions = promotions
    .filter((promo) => promo?.status !== "EXPIRED")
    .slice()
    .sort((a, b) => {
      const rank = (status) => {
        if (status === "ACTIVE") return 0;
        if (status === "SCHEDULED") return 1;
        return 2;
      };
      return rank(a?.status) - rank(b?.status);
    });
  const expiredPromotions = promotions.filter(
    (promo) => promo?.status === "EXPIRED",
  );
  const displayPromo = pickDisplayPromotion(currentPromotions);
  const isFlatSalePrice = isFlatPromoType(displayPromo);
  const displayPromoId = displayPromo?.promoId ?? displayPromo?.id ?? null;
  const displayPromoType = displayPromo?.promoType ?? null;
  const displayPromoDiscount = displayPromo?.discountValue ?? null;
  const displayPromoMinQuantity = displayPromo?.minQuantity ?? null;
  // FLAT sale price is independent of list price — omit it from the sync key.
  const salePriceListSync = isFlatSalePrice ? null : sellingPrice;

  const isMarginDrivenRef = React.useRef(false);
  const isSaleMarginDrivenRef = React.useRef(false);
  const lastEditedFieldRef = React.useRef("price");
  const lastEditedSaleFieldRef = React.useRef("price");
  const setComparePricePopUpRef = React.useRef(setComparePricePopUp);
  setComparePricePopUpRef.current = setComparePricePopUp;

  useEffect(() => {
    if (pricing?.Product !== undefined) {
      setSellingPrice(pricing.Product.price);
      setSavedSellingPrice(pricing.Product.price);
    } else if (product?.pricing?.price != null) {
      setSellingPrice(product.pricing.price);
      setSavedSellingPrice(product.pricing.price);
    }
  }, [pricing, product?.pricing?.price]);

  useEffect(() => {
    return () => {
      setComparePricePopUpRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (isMarginDrivenRef.current) {
      isMarginDrivenRef.current = false;
      return;
    }
    setProfitMargin(calcProfitMargin(costPrice, sellingPrice, { vatExempt }));
  }, [costPrice, sellingPrice, vatExempt]);

  useEffect(() => {
    if (!displayPromo) {
      setSalePrice("");
      setSavedSalePrice(null);
      setSaleProfitMargin("");
      return;
    }
    // Keep margin-driven FLAT price edits; the salePrice effect clears the flag.
    if (isSaleMarginDrivenRef.current) return;

    const calculated = getCalculatedPromoSalePrice(
      salePriceListSync,
      displayPromo,
    );
    if (calculated == null) {
      setSalePrice("");
      setSavedSalePrice(null);
      return;
    }
    const priceStr = String(calculated);
    setSalePrice(priceStr);
    setSavedSalePrice(priceStr);
  }, [
    displayPromo,
    displayPromoId,
    displayPromoType,
    displayPromoDiscount,
    displayPromoMinQuantity,
    salePriceListSync,
  ]);

  useEffect(() => {
    if (isSaleMarginDrivenRef.current) {
      isSaleMarginDrivenRef.current = false;
      return;
    }
    setSaleProfitMargin(calcProfitMargin(costPrice, salePrice, { vatExempt }));
  }, [costPrice, salePrice, vatExempt]);

  const fetchPromotions = useCallback(() => {
    if (!product?.gtin || !stationId) return;

    GetPromotions({
      gtin: product.gtin,
      branch: stationId,
      statuses: "ACTIVE,SCHEDULED,EXPIRED,DISABLED,DRAFT",
    })
      .then((data) => setPromotions(data || []))
      .catch(() => console.log("error"));
  }, [product, stationId]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const notifyPromotionsChanged = useCallback(() => {
    fetchPromotions();
    onPromotionsChanged?.();
  }, [fetchPromotions, onPromotionsChanged]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [promotions, endingPromoId]);

  const changePrice = async (productsToChange, price = sellingPrice) => {
    try {
      await applySequentially(productsToChange, async (prod) => {
        await setPrice(stationId, prod.gtin, price);
      });
      showAlert({
        msgTitle: "מחיר המכירה עודכן בהצלחה",
        msgType: "success",
      });
      setSavedSellingPrice(price);
      setShowPricePopUp(false);
      onPriceSaved?.(productsToChange, price);
    } catch (error) {
      console.error("Error updating prices:", error);
      showAlert({ msgTitle: "שגיאה בעדכון מחיר המכירה", msgType: "error" });
    }
  };

  const handleSellingPriceChange = (value) => {
    lastEditedFieldRef.current = "price";
    setSellingPrice(value);
  };

  const applyMarginToSellingPrice = (marginValue) => {
    if (costPrice == null || marginValue === "") return null;
    return effectivePriceFromMargin(costPrice, marginValue, { vatExempt });
  };

  const handleMarginChange = (value) => {
    lastEditedFieldRef.current = "margin";
    setProfitMargin(value);
    const newListPrice = applyMarginToSellingPrice(value);
    if (newListPrice == null) return;
    isMarginDrivenRef.current = true;
    setSellingPrice(String(newListPrice));
  };

  const saveSellingPrice = (price) => {
    if (price === "" || price == null) return;
    const parsed = parseFloat(price);
    if (!Number.isFinite(parsed)) return;
    const priceStr = String(roundMoney(parsed));
    if (priceStr !== String(sellingPrice)) setSellingPrice(priceStr);
    if (pricing?.Product?.price === priceStr) return;
    if (familyCategory) setShowPricePopUp(true);
    else changePrice([product], priceStr);
  };

  const handleSaveSellingPrice = () => {
    if (costPrice == null || lastEditedFieldRef.current !== "margin") {
      saveSellingPrice(sellingPrice);
      return;
    }
    const newListPrice = applyMarginToSellingPrice(profitMargin);
    if (newListPrice == null) {
      showAlert({ msgTitle: "אחוז הרווח שהוזן אינו תקין", msgType: "error" });
      return;
    }
    const priceStr = String(newListPrice);
    if (priceStr !== String(sellingPrice)) {
      isMarginDrivenRef.current = true;
      setSellingPrice(priceStr);
    }
    saveSellingPrice(newListPrice);
  };

  /* An unusable margin must keep the button clickable so the existing
     validation alert can still fire. */
  const marginPriceInvalid =
    costPrice != null &&
    profitMargin !== "" &&
    applyMarginToSellingPrice(profitMargin) == null;
  const sellingPriceUnchanged =
    !marginPriceInvalid && isSamePriceValue(sellingPrice, savedSellingPrice);
  const sellingPriceSaveDisabled =
    (costPrice == null
      ? sellingPrice === "" || sellingPrice == null
      : profitMargin === "") || sellingPriceUnchanged;

  const sellingPricesHeader = (
    <div className="field-group field-group--primary">
      <label className="field-label" htmlFor="selling-price">
        מחיר מכירה
      </label>
      <input
        id="selling-price"
        type="number"
        step="0.01"
        value={sellingPrice}
        onChange={(e) => handleSellingPriceChange(e.target.value)}
        className="number-input price-input highlighted"
        placeholder="0.00"
      />
    </div>
  );

  const sellingPriceSaveButton = (
    <button
      type="button"
      className="ui-btn-brand price-save-btn"
      onClick={handleSaveSellingPrice}
      disabled={sellingPriceSaveDisabled}
      aria-label="שמור מחיר מכירה"
      title={sellingPriceUnchanged ? "אין שינוי לשמירה" : "שמור מחיר מכירה"}
    >
      <Check size={20} strokeWidth={2.5} aria-hidden />
    </button>
  );

  const profitMarginRow = (
    <div className="field-group prices-fields-row__margin">
      <label className="field-label" htmlFor="profit-margin">
        <span className="field-label__main">אחוז רווח</span>
        <span className="field-hint field-hint--inline">
          {getProfitMarginVatHint(vatExempt)}
        </span>
      </label>
      <div className="input-with-suffix">
        <input
          id="profit-margin"
          type="number"
          step="0.01"
          value={profitMargin}
          onChange={(e) => handleMarginChange(e.target.value)}
          className="number-input"
          placeholder="0.00"
          disabled={costPrice == null}
        />
        <span className="input-suffix" aria-hidden>
          %
        </span>
      </div>
    </div>
  );

  const applyMarginToSalePrice = (marginValue) => {
    if (costPrice == null || marginValue === "") return null;
    return effectivePriceFromMargin(costPrice, marginValue, { vatExempt });
  };

  const handleSalePriceChange = (value) => {
    if (!isFlatSalePrice) return;
    lastEditedSaleFieldRef.current = "price";
    setSalePrice(value);
  };

  const handleSaleMarginChange = (value) => {
    if (!isFlatSalePrice) return;
    lastEditedSaleFieldRef.current = "margin";
    setSaleProfitMargin(value);
    if (costPrice == null || value === "") return;
    const newSalePrice = applyMarginToSalePrice(value);
    if (newSalePrice == null) return;
    isSaleMarginDrivenRef.current = true;
    setSalePrice(String(newSalePrice));
  };

  const handleSaveSalePrice = async () => {
    if (!isFlatSalePrice || !displayPromo?.promoId) return;

    let priceToSave = salePrice;
    if (costPrice != null && lastEditedSaleFieldRef.current === "margin") {
      const derived = applyMarginToSalePrice(saleProfitMargin);
      if (derived == null) {
        showAlert({ msgTitle: "אחוז הרווח שהוזן אינו תקין", msgType: "error" });
        return;
      }
      priceToSave = String(derived);
      if (priceToSave !== String(salePrice)) {
        isSaleMarginDrivenRef.current = true;
        setSalePrice(priceToSave);
      }
    }

    const parsed = parseFloat(priceToSave);
    if (!Number.isFinite(parsed) || parsed < 0) {
      showAlert({ msgTitle: "מחיר מבצע לא תקין", msgType: "error" });
      return;
    }
    const priceStr = String(roundMoney(parsed));
    if (isSamePriceValue(priceStr, savedSalePrice)) return;

    setSavingSalePrice(true);
    try {
      await updatePromotion(displayPromo.promoId, {
        discountValue: roundMoney(parsed),
      });
      setSalePrice(priceStr);
      setSavedSalePrice(priceStr);
      showAlert({ msgTitle: "מחיר המבצע עודכן בהצלחה", msgType: "success" });
      notifyPromotionsChanged();
    } catch (error) {
      console.error("Error updating sale price:", error);
      showAlert({ msgTitle: "שגיאה בעדכון מחיר המבצע", msgType: "error" });
    } finally {
      setSavingSalePrice(false);
    }
  };

  const openDisplayPromoEditor = () => {
    if (!displayPromo) return;
    setEditingPromo(displayPromo);
    setShowPromotionPopUp(true);
  };

  const saleMarginPriceInvalid =
    isFlatSalePrice &&
    costPrice != null &&
    saleProfitMargin !== "" &&
    applyMarginToSalePrice(saleProfitMargin) == null;
  const salePriceUnchanged =
    !saleMarginPriceInvalid && isSamePriceValue(salePrice, savedSalePrice);
  const salePriceSaveDisabled =
    savingSalePrice ||
    !displayPromo?.promoId ||
    (costPrice == null
      ? salePrice === "" || salePrice == null
      : saleProfitMargin === "") ||
    salePriceUnchanged;

  const promoSaleRow = displayPromo ? (
    <>
      <div className="field-group field-group--promo-sale">
        <label className="field-label" htmlFor="promo-sale-price">
          <span className="field-label__main">מחיר מבצע</span>
        </label>
        <div className="promo-sale-price-control">
          <input
            id="promo-sale-price"
            type="number"
            step="0.01"
            value={salePrice}
            onChange={(e) => handleSalePriceChange(e.target.value)}
            className={`number-input price-input${isFlatSalePrice ? " highlighted" : ""}`}
            placeholder="0.00"
            readOnly={!isFlatSalePrice}
            disabled={!isFlatSalePrice}
          />
          {!isFlatSalePrice ? (
            <button
              type="button"
              className="promo-sale-edit-btn"
              onClick={openDisplayPromoEditor}
              aria-label="עריכת מבצע ממחיר מבצע"
              title="עריכת מבצע"
            >
              <Pencil size={16} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <div className="field-group prices-fields-row__sale-margin">
        <label className="field-label" htmlFor="promo-sale-margin">
          <span className="field-label__main">אחוז רווח במבצע</span>
          <span className="field-hint field-hint--inline">
            {getProfitMarginVatHint(vatExempt)}
          </span>
        </label>
        <div className="input-with-suffix">
          <input
            id="promo-sale-margin"
            type="number"
            step="0.01"
            value={saleProfitMargin}
            onChange={(e) => handleSaleMarginChange(e.target.value)}
            className="number-input"
            placeholder="0.00"
            readOnly={!isFlatSalePrice}
            disabled={!isFlatSalePrice || costPrice == null}
          />
          <span className="input-suffix" aria-hidden>
            %
          </span>
        </div>
      </div>

      {isFlatSalePrice ? (
        <button
          type="button"
          className={`ui-btn-brand price-save-btn${
            savingSalePrice ? " price-save-btn--saving" : ""
          }`}
          onClick={handleSaveSalePrice}
          disabled={salePriceSaveDisabled}
          aria-label="שמור מחיר מבצע"
          title={
            savingSalePrice
              ? "שומר מחיר מבצע..."
              : salePriceUnchanged
                ? "אין שינוי לשמירה"
                : "שמור מחיר מבצע"
          }
        >
          <Check size={20} strokeWidth={2.5} aria-hidden />
        </button>
      ) : (
        <span
          className="price-save-btn price-save-btn--spacer"
          data-testid="promo-sale-save-spacer"
          aria-hidden
        />
      )}
    </>
  ) : null;

  const getPromoTypeName = (promoType) => {
    switch (promoType) {
      case "FLAT":
        return "מחיר מבצע";
      case "PERCENT":
        return "אחוז הנחה";
      case "QUANTITY_PRICE":
        return "X ב-Y";
      case "PACKAGE_PRICE":
        return "מחיר מארז";
      default:
        return promoType || "";
    }
  };

  const getPromoValue = (promo) => {
    if (!promo) return "";
    switch (promo.promoType) {
      case "FLAT":
        return `${Number(promo.discountValue).toFixed(2)} ₪`;
      case "PERCENT":
        return `${promo.discountValue}%`;
      case "QUANTITY_PRICE":
      case "PACKAGE_PRICE":
        return `${promo.minQuantity} ב-${Number(promo.discountValue).toFixed(2)} ₪`;
      default:
        return "";
    }
  };

  const handleEndOffer = async (promo) => {
    const promoId = promo?.promoId ?? promo?.id;
    if (!promoId || endingPromoId != null) return;

    setEndingPromoId(promoId);
    try {
      await updatePromotion(promoId, buildEndOfferPayload(promo));
      showAlert({ msgTitle: "המבצע הסתיים בהצלחה", msgType: "success" });
      notifyPromotionsChanged();
    } catch (error) {
      console.error("Error ending promotion:", error);
      showAlert({
        msgTitle: "לא ניתן לסיים את המבצע",
        msgText: getPromotionApiErrorMessage(error, "שגיאה בסיום מבצע"),
        msgType: "error",
      });
    } finally {
      setEndingPromoId(null);
    }
  };

  const requestEndOffer = (promo) => {
    showAlertWithConfirmCancel({
      msgTitle: "האם אתה בטוח שברצונך לסיים את המבצע?",
      confirmText: "סיים מבצע",
      cancelText: "חזרה",
      onConfirm: () => handleEndOffer(promo),
    });
  };

  const PROMO_STATUS_MAP = {
    ACTIVE: { label: "פעיל", modifier: "active" },
    SCHEDULED: { label: "מתוזמן", modifier: "scheduled" },
    EXPIRED: { label: "פג תוקף", modifier: "expired" },
    DISABLED: { label: "מוקפא", modifier: "disabled" },
    DRAFT: { label: "טיוטה", modifier: "draft" },
  };

  const getPromoDateRange = (promo) => {
    const fmt = (d) => formatExpirationDate(d) || "";
    const start = fmt(promo.startDate);
    const end = fmt(promo.endDate);
    if (start && end) return `${start} — ${end}`;
    if (start) return `מ-${start}`;
    if (end) return `עד ${end}`;
    return "";
  };

  const renderPromoSummaryItem = (promo) => {
    const statusInfo = PROMO_STATUS_MAP[promo.status] || {
      label: promo.status,
      modifier: "",
    };
    const dateRange = getPromoDateRange(promo);
    const condition = getPromoMinQuantityNote(promo);
    const promoId = promo.promoId ?? promo.id;
    const canEditOffer = promo.status !== "EXPIRED";
    const canEndOffer = promo.status !== "EXPIRED";
    const isEnding = endingPromoId != null && endingPromoId === promoId;

    return (
      <li key={promoId} className="promo-summary-item">
        <div className="promo-summary-item__top">
          <span className="promo-summary-item__type-tag">
            {getPromoTypeName(promo.promoType)}
          </span>
          <span className="promo-summary-item__value">
            {getPromoValue(promo)}
          </span>
          {condition && (
            <span className="promo-summary-item__condition">{condition}</span>
          )}
          <span
            className={`promo-summary-item__status promo-summary-item__status--${statusInfo.modifier}`}
          >
            {statusInfo.label}
          </span>
          {canEditOffer ? (
            <button
              type="button"
              className="promo-summary-item__edit-btn"
              onClick={() => {
                setEditingPromo(promo);
                setShowPromotionPopUp(true);
              }}
              aria-label="עריכת מבצע"
              title="עריכת מבצע"
            >
              <Pencil size={16} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          {canEndOffer ? (
            <button
              type="button"
              className="promo-summary-item__end-btn"
              onClick={() => requestEndOffer(promo)}
              disabled={isEnding || endingPromoId != null}
              aria-label="סיים מבצע"
              data-tip
              data-for="comparePriceEndOfferTip"
            >
              <StopCircle size={15} strokeWidth={2.25} aria-hidden />
            </button>
          ) : null}
        </div>
        {(promo.description || dateRange) && (
          <div className="promo-summary-item__bottom">
            {promo.description && (
              <span className="promo-summary-item__desc">
                {promo.description}
              </span>
            )}
            {dateRange && (
              <span className="promo-summary-item__dates">
                <Calendar size={13} strokeWidth={2} aria-hidden />
                <span className="promo-summary-item__dates-text">
                  {dateRange}
                </span>
              </span>
            )}
          </div>
        )}
      </li>
    );
  };

  const promotionsSlot = (
    <div className="prices-promotions-slot">
      <div className="prices-tab-toolbar">
        <button
          type="button"
          className={`promo-history-btn${showPromoHistory ? " promo-history-btn--active" : ""}`}
          onClick={() => setShowPromoHistory((open) => !open)}
          aria-pressed={showPromoHistory}
          aria-label="היסטוריית מבצעים"
          title="היסטוריית מבצעים"
        >
          <History size={16} strokeWidth={2.25} aria-hidden />
          <span>הצג היסטורייה</span>
        </button>
        <button
          type="button"
          className="promo-add-btn"
          onClick={() => {
            setEditingPromo(null);
            setShowPromotionPopUp(true);
          }}
          aria-label="יצירת מבצע חדש"
          title="יצירת מבצע חדש"
        >
          <Plus size={18} strokeWidth={2.5} aria-hidden />
        </button>
      </div>
      <div className="promo-summary-scroll">
        {currentPromotions.length > 0 ? (
          <ul className="promo-summary-list">
            {currentPromotions.map(renderPromoSummaryItem)}
          </ul>
        ) : (
          <p className="promotions-empty-hint">אין מבצעים</p>
        )}
        {showPromoHistory && (
          <div className="promo-history-panel">
            <h4 className="promo-history-panel__title">היסטוריית מבצעים</h4>
            {expiredPromotions.length > 0 ? (
              <ul className="promo-summary-list">
                {expiredPromotions.map(renderPromoSummaryItem)}
              </ul>
            ) : (
              <p className="promotions-empty-hint">אין מבצעים </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const mainContent = (
    <>
      <div className="prices-main-row">
        <div className="prices-unified-col">
          <SupplierPricesSection
            product={product}
            onCostPriceChange={setCostPrice}
            familyCategory={familyCategory}
            sellingPricesHeader={sellingPricesHeader}
            profitMarginRow={profitMarginRow}
            sellingPriceSaveButton={sellingPriceSaveButton}
            promoSaleRow={promoSaleRow}
            promotionsSlot={promotionsSlot}
            sellingOffersCount={
              currentPromotions.filter(
                (promo) => (promo?.status ?? "ACTIVE") === "ACTIVE",
              ).length
            }
          />
        </div>

        <div className="compare-prices-col">
          <div className="section-card compare-prices-section">
            <h3 className="section-title section-title--compact">
              השוואת מחירים
            </h3>
            <PricesCompareTable selectedProd={product} showExpiration={false} />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div
        className={`compare-price-container${
          isModal ? " compare-price-container--modal" : ""
        }`}
      >
        {isModal ? (
          <>
            <header className="compare-price-modal-header">
              <h2 className="compare-price-modal-header__title">עריכת מחיר</h2>
              {productHeaderMetaParts.length ? (
                <p className="compare-price-modal-header__name">
                  {productHeaderMetaParts.join(" | ")}
                </p>
              ) : null}
              {product?.gtin ? (
                <p className="compare-price-modal-header__gtin">
                  ברקוד: {product.gtin}
                </p>
              ) : null}
            </header>
            <div className="compare-price-modal-scroll">{mainContent}</div>
          </>
        ) : (
          mainContent
        )}
      </div>

      {showPromotionPopUp && (
        <PopUp
          width="850px"
          close={() => {
            setShowPromotionPopUp(false);
            setEditingPromo(null);
          }}
        >
          <div className="compare-price-container compare-price-container--popup">
            <Promotion
              promo={editingPromo || 0}
              product={product}
              existingPromotions={promotions}
              close={() => {
                setShowPromotionPopUp(false);
                setEditingPromo(null);
              }}
              onCreated={notifyPromotionsChanged}
            />
          </div>
        </PopUp>
      )}

      {showPricePopUp && familyCategory && (
        <FamilyChangeDialog
          product={product}
          close={() => setShowPricePopUp(false)}
          onConfirm={changePrice}
          markedProducts={[product]}
          massage={"סמן את המוצרים שתרצה לשנות בנוסף אליו:"}
          slug={familyCategory.slug}
        />
      )}

      <ReactTooltip id="comparePriceEndOfferTip" place="top" effect="solid">
        סיים מבצע
      </ReactTooltip>
    </>
  );
};

export default ComparePricePopUp;
